/**
 * ActivityFeed - the "Recent team activity" list on the DQ Workbench Overview.
 * Each row reads as "<who> <action>" (blocked / ignored / noted, or
 * sync-confirmed fixed), with a market chip, the franchise name, a relative
 * timestamp, and a right-aligned status pill (or a teal "Fixed" pill when the
 * row has flipped to resolved - sync-confirmed).
 */
import React from 'react'
import { Box, Chip, Typography } from '@mui/material'

import type { ExceptionActivityItem } from '../../../../services/exceptionActivityApi'
import { formatRelativeTimestamp } from '../../../../utils/dateFormatters'
import { poolwerxColors } from '../../../../theme/colors'
import { dqBlockedPill } from '../../../../theme/dqStatusColors'
import MarketChip from './MarketChip'

interface Props {
  items: ExceptionActivityItem[]
  /** When set, each row becomes a button that opens the item's drawer.
   * Omit for a purely read-only feed. */
  onSelect?: (item: ExceptionActivityItem) => void
}

interface Line {
  who: string
  action: string
}

// Pick the most relevant actor + verb for the row from the item's signals.
const describe = (item: ExceptionActivityItem): Line => {
  if (item.status === 'blocked' && item.blocked_by?.name) {
    return { who: item.blocked_by.name, action: 'blocked this' }
  }
  if (item.status === 'ignored' && item.ignored_by?.name) {
    return { who: item.ignored_by.name, action: 'ignored this' }
  }
  if (item.latest_note_author?.name) {
    return { who: item.latest_note_author.name, action: 'added a note' }
  }
  if (item.status === 'resolved') {
    return { who: 'Sync', action: 'confirmed fixed' }
  }
  return { who: 'Someone', action: 'updated this' }
}

const STATUS_PILL: Record<string, { label: string; bg: string; color: string }> = {
  needs_review: { label: 'Needs review', bg: 'grey.200', color: 'text.secondary' },
  blocked: { label: 'Blocked', bg: dqBlockedPill.bg, color: dqBlockedPill.fg },
  ignored: { label: 'Ignored', bg: 'grey.200', color: 'text.secondary' },
}

const ActivityFeed: React.FC<Props> = ({ items, onSelect }) => (
  <Box>
    {items.map((item, idx) => {
      const line = describe(item)
      const ts = formatRelativeTimestamp(item.last_activity_at)
      const isFixed = item.status === 'resolved'
      const pill = STATUS_PILL[item.status]
      // Clickable rows open the drawer (in-page state, not navigation -
      // so onClick is correct here, not a router <Link>).
      const clickable = Boolean(onSelect)
      return (
        <Box
          key={`${item.fp_code}-${item.object_id}-${idx}`}
          {...(clickable
            ? {
                role: 'button',
                tabIndex: 0,
                onClick: () => onSelect!(item),
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSelect!(item)
                  }
                },
              }
            : {})}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 1,
            borderBottom: idx < items.length - 1 ? '1px solid' : 'none',
            borderColor: 'divider',
            ...(clickable
              ? {
                  cursor: 'pointer',
                  mx: -1,
                  px: 1,
                  borderRadius: 1,
                  transition: 'background-color 120ms ease',
                  '&:hover': { bgcolor: 'rgba(15,23,42,0.03)' },
                  '&:focus-visible': {
                    outline: `2px solid ${poolwerxColors.teal}`,
                    outlineOffset: -2,
                  },
                }
              : {}),
          }}
        >
          <MarketChip market={item.market} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" noWrap sx={{ color: 'text.primary' }}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {line.who}
              </Box>{' '}
              {line.action}
              {item.latest_note_snippet ? (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {': '}
                  {item.latest_note_snippet}
                </Box>
              ) : null}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }} title={ts.title}>
              {item.franchise_name} · {ts.label}
            </Typography>
          </Box>
          {isFixed ? (
            <Chip
              label="Fixed"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.68rem',
                fontWeight: 600,
                bgcolor: poolwerxColors.tealLight,
                color: poolwerxColors.darkCyan,
              }}
            />
          ) : pill ? (
            <Chip
              label={pill.label}
              size="small"
              sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, bgcolor: pill.bg, color: pill.color }}
            />
          ) : null}
        </Box>
      )
    })}
  </Box>
)

export default ActivityFeed
